import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { apiFetch, callApi } from '../api';
import usePolling from '../utils/usePolling';
import Modal from '../components/Modal';
import InfoTip from '../components/InfoTip';
import SearchableSelect from '../components/SearchableSelect';
import { DEFAULT_TAG, MAX_LEN, MAX_TAG_LEN, cleanTagInput, plainSummary } from '../utils/servicePreview';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, EmptyState, Field, Icon, PageHeader, Pagination, ProgressBar, TableSkeleton, Tabs, cx,
} from '../components/ui';

// Message-vs-workspace-refresh split is now done server-side via the
// /runs?kind= param, so the frontend no longer needs the UPDATE_MESSAGE
// marker string that bulk_update.py stamps on its rows.

// Run and schedule states as the API reports them, each with its badge. A state missing
// from these maps still renders, as a neutral badge carrying the raw string.
const RUN_STATUS = {
  queued:    { tone: 'neutral', label: 'Queued' },
  running:   { tone: 'info', label: 'Running' },
  completed: { tone: 'ok', label: 'Completed' },
  failed:    { tone: 'danger', label: 'Failed' },
};

const SCHEDULE_STATE = {
  active:    { tone: 'ok', label: 'Active' },
  paused:    { tone: 'warn', label: 'Paused' },
  completed: { tone: 'neutral', label: 'Completed' },
  cancelled: { tone: 'danger', label: 'Cancelled' },
};

// The toast after a state change. It was built as `${action}d`, which turned "cancel"
// into "canceld".
const STATE_DONE = { pause: 'paused', resume: 'resumed', cancel: 'cancelled' };

function stateBadge(map, value, pulse) {
  const s = map[value] || { tone: 'neutral', label: value || 'Unknown' };
  return <Badge tone={s.tone} dot pulse={pulse}>{s.label}</Badge>;
}

// Mirrors backend api/routes/broadcast.py _VALID_SOURCES. Kept in
// display order rather than alphabetic so the dropdown reads in the
// same order an admin thinks about cohorts: organic admin → paid →
// coupon-onboarded.
const SOURCE_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'website', label: 'Website' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'coupon', label: 'Coupon' },
];

// Sentinel coupon code for users who redeemed BEFORE migration 009.
// Backfilled to 'legacy' so this option lets the admin target that
// cohort distinctly from any named coupon's redemptions.
const LEGACY_COUPON_CODE = 'legacy';

// What an operator calls each channel (backend services/channel_identities.py
// CHANNEL_LABELS). The channel picker takes its list and labels from the API; this names a
// channel stored on a past run when that list has not loaded.
const CHANNEL_LABELS = {
  telegram: 'Telegram', website: 'RossHub', teams: 'Microsoft Teams', rossapp: 'Ross App',
  'rossapp-pen': 'Ross App (pentest)', voice: 'Phone',
};
const channelLabel = (value) => CHANNEL_LABELS[value] || value;

// Build a short human-readable summary of the persisted filter for the
// Recent broadcasts table. Returns 'All users' when nothing was filtered.
function summarizeFilter(summary, tiers) {
  if (!summary) return 'All users';
  const parts = [];
  if (summary.channel) parts.push(`on ${channelLabel(summary.channel)}`);
  if (summary.tier_id != null) {
    const t = tiers.find(x => x.id === summary.tier_id);
    parts.push(`tier: ${t ? t.name : `#${summary.tier_id}`}`);
  }
  if (summary.source) parts.push(`source: ${summary.source}`);
  if (summary.coupon_code) parts.push(`coupon: ${summary.coupon_code}`);
  return parts.length ? parts.join(' · ') : 'All users';
}

// The same filter as separate conditions, for the summary card's chips: how many things
// narrow the audience is easier to see as chips than inside one joined string.
function filterParts(summary, tiers) {
  if (!summary) return [];
  const parts = [];
  if (summary.channel) parts.push({ key: 'channel', label: 'Channel', value: channelLabel(summary.channel) });
  if (summary.tier_id != null) {
    const t = tiers.find(x => x.id === summary.tier_id);
    parts.push({ key: 'tier', label: 'Tier', value: t ? t.name : `#${summary.tier_id}` });
  }
  if (summary.source) parts.push({ key: 'source', label: 'Source', value: summary.source });
  if (summary.coupon_code) parts.push({ key: 'coupon', label: 'Coupon', value: summary.coupon_code });
  return parts;
}

// A message as Telegram shows it. The HTML comes from the platform's own converter
// (shared/message_format.py), which escapes everything it did not write itself, so it is
// safe to insert. A click on a link is kept on this page.
function RenderedMessage({ html, maxHeight }) {
  return (
    <div
      className="message-render"
      style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
      onClick={(e) => { if (e.target.closest('a')) e.preventDefault(); }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// The server's rendering of the message being written: the tag line in bold and the
// markdown as Telegram will show it. Asked for 300 ms after the last keystroke.
function usePreview(message, tag) {
  const [preview, setPreview] = useState({ html: '', error: null });
  useEffect(() => {
    if (!message.trim()) {
      setPreview({ html: '', error: null });
      return undefined;
    }
    let live = true;
    const t = setTimeout(async () => {
      try {
        const resp = await apiFetch('/api/admin/broadcast/preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message, tag }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!live) return;
        if (resp.ok) setPreview({ html: data.html || '', error: null });
        else setPreview({ html: '', error: data.detail || `HTTP ${resp.status}` });
      } catch {
        if (live) setPreview({ html: '', error: 'The preview could not be loaded' });
      }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [message, tag]);
  return preview;
}

// The tag's words between two fixed brackets. The brackets cannot be typed or removed.
function TagInput({ id, value, onChange, describedBy }) {
  return (
    <div className="tag-brackets">
      <span className="tag-bracket" aria-hidden="true">[</span>
      <input
        id={id}
        className="form-input"
        value={value}
        maxLength={MAX_TAG_LEN}
        onChange={(e) => onChange(cleanTagInput(e.target.value))}
        aria-describedby={describedBy}
        spellCheck={false}
      />
      <span className="tag-bracket" aria-hidden="true">]</span>
    </div>
  );
}

const FORMAT_HINT = '**bold**, _italic_, ~~strike~~, `code`, [link](https://…)';

// datetime-local <input> value (naive local wall-clock) -> UTC ISO string.
// The admin picks a local time; JS reads it as local and toISOString()
// converts to UTC, which the backend stores/compares in UTC.
function localInputToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// UTC ISO string -> value for a datetime-local <input> (local wall-clock,
// minute precision, no seconds/zone).
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// "19 Sep 2026, 14:05" in the reader's local time, the clock the schedule form takes its
// input in. A month name cannot be misread as D/M vs M/D. The full stamp is the tooltip.
const SHORT_WHEN = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };

function formatWhen(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, SHORT_WHEN);
}

function fullWhen(iso) {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// A stored message in a one-line table cell: its body as plain text (the API returns the
// tag and the body apart), with the tag as a badge when it is not the default one. A click
// opens it as it was sent.
function MessageCell({ row, maxWidth, onOpen }) {
  const body = plainSummary(row.body ?? row.message);
  const custom = row.tag && row.tag !== DEFAULT_TAG;
  return (
    <button type="button" className="btn btn-link text-left" onClick={() => onOpen(row)}
            title="Show the message as it was sent" style={{ maxWidth }}>
      <span className="stack gap-1 items-start">
        {custom && <Badge tone="outline">[{row.tag}]</Badge>}
        <span className="truncate text-2" style={{ maxWidth }}>{body}</span>
      </span>
    </button>
  );
}

const RUNS_PAGE_SIZE = 10;

export default function Broadcast() {
  const [activeTab, setActiveTab] = useState('compose');

  // Filter state shared by the send form. Empty string = no constraint.
  const [tiers, setTiers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [selectedTierId, setSelectedTierId] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedCouponCode, setSelectedCouponCode] = useState('');

  // Channels a broadcast can be aimed at in this environment; '' = each user on their own.
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState('');

  // Send-form state.
  const [message, setMessage] = useState('');
  const [tag, setTag] = useState(DEFAULT_TAG);
  const [viewing, setViewing] = useState(null);           // run or schedule shown in full
  const [sendMode, setSendMode] = useState('now');       // 'now' | 'schedule'
  const [firstTriggerAt, setFirstTriggerAt] = useState('');
  const [repeatEveryDays, setRepeatEveryDays] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const [sending, setSending] = useState(false);

  // Workspace-refresh state.
  const [updating, setUpdating] = useState(false);

  // Runs — one paged list per kind (each tab owns its own table).
  const [messageRuns, setMessageRuns] = useState([]);
  const [messageRunsPage, setMessageRunsPage] = useState(1);
  const [messageRunsTotal, setMessageRunsTotal] = useState(0);
  const [updateRuns, setUpdateRuns] = useState([]);
  const [updateRunsPage, setUpdateRunsPage] = useState(1);
  const [updateRunsTotal, setUpdateRunsTotal] = useState(0);
  const [hasActiveUpdate, setHasActiveUpdate] = useState(false);
  const [hasActiveBroadcast, setHasActiveBroadcast] = useState(false);
  // False until the first /runs answer, so the tables show a skeleton rather than "none yet".
  const [runsLoaded, setRunsLoaded] = useState(false);

  // Scheduled / recurrent messages.
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoaded, setSchedulesLoaded] = useState(false);
  const [editing, setEditing] = useState(null);   // schedule row being edited, or null

  // Fetch tiers + coupons once for the filter dropdowns. Failures are
  // silent — the filter UI shows an empty list and the admin can still
  // send a global broadcast.
  useEffect(() => {
    (async () => {
      const [tResp, cResp, chResp] = await Promise.all([
        apiFetch('/api/tiers').catch(() => null),
        apiFetch('/api/coupons').catch(() => null),
        apiFetch('/api/admin/broadcast/channels').catch(() => null),
      ]);
      if (tResp && tResp.ok) {
        const data = await tResp.json().catch(() => null);
        setTiers(Array.isArray(data) ? data : (data?.tiers || []));
      }
      if (cResp && cResp.ok) {
        const data = await cResp.json().catch(() => null);
        setCoupons(Array.isArray(data) ? data : []);
      }
      if (chResp && chResp.ok) {
        const data = await chResp.json().catch(() => null);
        setChannels((data && data.channels) || []);
      }
    })();
  }, []);

  // When the admin picks a non-coupon source, wipe any selected coupon
  // code — the backend rejects coupon_code under a non-coupon source.
  useEffect(() => {
    if (selectedSource && selectedSource !== 'coupon' && selectedCouponCode) {
      setSelectedCouponCode('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSource]);

  const couponPickerEnabled = !selectedSource || selectedSource === 'coupon';

  const loadRuns = useCallback(async () => {
    const [mResp, uResp] = await Promise.all([
      apiFetch(`/api/admin/broadcast/runs?kind=message&page=${messageRunsPage}&limit=${RUNS_PAGE_SIZE}`).catch(() => null),
      apiFetch(`/api/admin/broadcast/runs?kind=update&page=${updateRunsPage}&limit=${RUNS_PAGE_SIZE}`).catch(() => null),
    ]);
    if (mResp && mResp.ok) {
      const data = await mResp.json().catch(() => null);
      setMessageRuns((data && data.runs) || []);
      setMessageRunsTotal((data && data.total) || 0);
      setHasActiveUpdate(!!(data && data.has_active_update));
      setHasActiveBroadcast(!!(data && data.has_active_broadcast));
    }
    if (uResp && uResp.ok) {
      const data = await uResp.json().catch(() => null);
      setUpdateRuns((data && data.runs) || []);
      setUpdateRunsTotal((data && data.total) || 0);
      setHasActiveUpdate(!!(data && data.has_active_update));
      setHasActiveBroadcast(!!(data && data.has_active_broadcast));
    }
    setRunsLoaded(true);
  }, [messageRunsPage, updateRunsPage]);

  const loadSchedules = useCallback(async () => {
    const resp = await apiFetch('/api/admin/broadcast/schedules').catch(() => null);
    if (resp && resp.ok) {
      const data = await resp.json().catch(() => null);
      setSchedules((data && data.schedules) || []);
    }
    setSchedulesLoaded(true);
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const updateActive = hasActiveUpdate;
  const broadcastActive = hasActiveBroadcast;
  const anyActive = updateActive || broadcastActive;

  // Auto-refresh every 5s while any run is queued/running and the tab is visible
  // (utils/usePolling). A page change reloads through the effect above.
  usePolling(loadRuns, anyActive ? 5000 : 0);

  const charsLeft = MAX_LEN - message.length;
  const tagValid = tag.trim().length > 0;
  const messageValid = message.trim().length > 0 && message.length <= MAX_LEN && tagValid;
  const preview = usePreview(message, tag.trim() || DEFAULT_TAG);

  // Build the filter payload as the backend expects (BroadcastFilter).
  const buildFilterPayload = () => {
    const flt = {};
    if (selectedChannel) flt.channel = selectedChannel;
    if (selectedTierId !== '' && selectedTierId != null) flt.tier_id = Number(selectedTierId);
    if (selectedSource) flt.source = selectedSource;
    if (selectedCouponCode && couponPickerEnabled) flt.coupon_code = selectedCouponCode;
    return Object.keys(flt).length ? flt : null;
  };

  const filterSummary = summarizeFilter(buildFilterPayload(), tiers);
  const hasFilter = filterSummary !== 'All users';
  const audienceParts = filterParts(buildFilterPayload(), tiers);
  const channelName = selectedChannel ? channelLabel(selectedChannel) : null;

  // The server's rendering when it has arrived; until then (or if it failed) the text as
  // it will be stored, so a preview is never blank.
  const previewBlock = (maxHeight) => (preview.html
    ? <RenderedMessage html={preview.html} maxHeight={maxHeight} />
    : (
      <pre className="code-block pre-wrap break m-0" style={{ maxHeight, overflowY: 'auto' }}>
        {`[${tag.trim() || DEFAULT_TAG}]\n\n${message.trim()}`}
      </pre>
    ));

  const resetForm = () => {
    setMessage('');
    setTag(DEFAULT_TAG);
    setFirstTriggerAt('');
    setRepeatEveryDays('');
    setSelectedChannel('');
    setSelectedTierId('');
    setSelectedSource('');
    setSelectedCouponCode('');
  };

  const clearFilters = () => {
    setSelectedChannel('');
    setSelectedTierId('');
    setSelectedSource('');
    setSelectedCouponCode('');
  };

  const sendImmediate = async () => {
    setSending(true);
    try {
      const filter = buildFilterPayload();
      const body = { message: message.trim(), tag: tag.trim() };
      if (filter) body.filter = filter;
      const data = await callApi('/api/admin/broadcast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }, { errorMsg: 'Broadcast failed' });
      if (data) {
        toast.success(`Broadcast queued, ${data.target_count} recipient(s)`);
        resetForm();
        // The run list used to sit under the form. It has its own tab now, so follow the
        // run there instead of leaving the admin on an emptied form.
        setActiveTab('sent');
        await loadRuns();
      }
    } finally {
      setSending(false);
    }
  };

  // One confirmation, naming who gets it and that it cannot be taken back, with the exact
  // text. It stays on screen only until the admin answers: the send button beside the form
  // then shows the request in flight and cannot be pressed twice.
  const confirmAndSend = async () => {
    if (!await confirm({
      title: hasFilter ? 'Send this message to the selected users?' : 'Send this message to every active user?',
      body: (
        <>
          <p>
            It goes to{' '}
            {hasFilter
              ? <>users matching <strong>{filterSummary}</strong></>
              : <strong>every active user</strong>}{' '}
            {channelName
              ? <>paired on {channelName}, on {channelName} only.</>
              : 'with at least one paired channel, on the channel each of them last used.'}
          </p>
          <p><strong>It cannot be cancelled once it starts.</strong></p>
          {previewBlock(200)}
        </>
      ),
      confirmLabel: 'Send broadcast',
    })) return;
    await sendImmediate();
  };

  // Client-side schedule validation mirroring the backend rules.
  const scheduleValidationError = () => {
    if (!tagValid) return 'The tag needs some words between the brackets';
    if (!messageValid) return 'Message body is required';
    const iso = localInputToIso(firstTriggerAt);
    if (!iso) return 'Pick a valid first trigger date & time';
    if (new Date(iso).getTime() <= Date.now()) return 'First trigger must be in the future';
    if (repeatEveryDays !== '') {
      const n = Number(repeatEveryDays);
      if (!Number.isInteger(n) || n < 1) return 'Repeat interval must be a whole number of days ≥ 1';
    }
    return null;
  };

  const createSchedule = async () => {
    const err = scheduleValidationError();
    if (err) { toast.error(err); return; }
    setSending(true);
    try {
      const filter = buildFilterPayload();
      const body = {
        message: message.trim(),
        tag: tag.trim(),
        first_trigger_at: localInputToIso(firstTriggerAt),
      };
      if (repeatEveryDays !== '') body.repeat_every_days = Number(repeatEveryDays);
      if (filter) body.filter = filter;
      const data = await callApi('/api/admin/broadcast/schedules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }, { errorMsg: 'Could not schedule message' });
      if (data) {
        toast.success(
          data.repeat_every_days
            ? `Scheduled, first fire ${new Date(data.next_trigger_at).toLocaleString()}, repeats every ${data.repeat_every_days} day(s)`
            : `Scheduled for ${new Date(data.next_trigger_at).toLocaleString()}`
        );
        resetForm();
        setActiveTab('scheduled');
        await loadSchedules();
      }
    } finally {
      setSending(false);
    }
  };

  const triggerUpdate = async () => {
    setUpdating(true);
    try {
      const data = await callApi('/api/admin/broadcast/update-all', {
        method: 'POST',
      }, { errorMsg: 'Update failed to start' });
      if (data) {
        toast.success(`Update queued, ${data.target_count} user(s)`);
        await loadRuns();
      }
    } finally {
      setUpdating(false);
    }
  };

  // ── Schedule row actions ──────────────────────────────────────────────
  const setScheduleState = async (id, action) => {
    const data = await callApi(`/api/admin/broadcast/schedules/${id}/state`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    }, { successMsg: `Message ${STATE_DONE[action] || `${action}d`}`, errorMsg: `Could not ${action}` });
    if (data) await loadSchedules();
  };

  // Cancelling is terminal (the backend refuses to resume or edit a cancelled message),
  // so unlike Pause it asks first.
  const cancelSchedule = async (id) => {
    if (!await confirm({
      title: 'Cancel this scheduled message?',
      body: (
        <p>
          It stops firing for good and cannot be resumed or edited. It stays in the list,
          and the runs it already sent are kept.
        </p>
      ),
      confirmLabel: 'Cancel schedule',
      cancelLabel: 'Keep it',
    })) return;
    await setScheduleState(id, 'cancel');
  };

  const deleteSchedule = async (id) => {
    if (!await confirm({
      title: 'Delete this scheduled message?',
      body: <p>It stops firing and disappears from the list. Already-sent runs are kept.</p>,
      confirmLabel: 'Delete schedule',
    })) return;
    const data = await callApi(`/api/admin/broadcast/schedules/${id}`, {
      method: 'DELETE',
    }, { successMsg: 'Scheduled message deleted', errorMsg: 'Could not delete' });
    if (data) await loadSchedules();
  };

  const canSchedule = messageValid && !scheduleValidationError();

  // What the summary says about timing, from the form as it stands.
  const firstIso = localInputToIso(firstTriggerAt);
  const repeatN = Number(repeatEveryDays);
  const repeats = repeatEveryDays !== '' && Number.isInteger(repeatN) && repeatN >= 1;
  let delivery = null;
  if (sendMode === 'now') {
    delivery = channelName ? `Now, on ${channelName} only.` : 'Now, on the channel each user last wrote from.';
  }
  else if (firstIso && repeats) delivery = `Every ${repeatN} day${repeatN === 1 ? '' : 's'}, from ${formatWhen(firstIso)}.`;
  else if (firstIso) delivery = `Once, on ${formatWhen(firstIso)}.`;

  // Why the button is disabled, said next to it. Scheduling used to grey the button out
  // with no reason given: the toasts that explain it only run on a click, and a disabled
  // button never receives one.
  const blocker = !message.trim()
    ? 'Write a message first.'
    : !tagValid ? 'The tag needs some words between the brackets.'
      : sendMode === 'schedule' ? scheduleValidationError() : null;

  const liveSchedules = schedules.filter(s => s.state === 'active' || s.state === 'paused').length;
  const tabs = [
    { value: 'compose', label: 'Compose', icon: 'send' },
    { value: 'scheduled', label: 'Scheduled', icon: 'calendar', count: liveSchedules > 0 ? liveSchedules : undefined },
    { value: 'sent', label: 'Sent', icon: 'history' },
    { value: 'refresh', label: 'Workspace refresh', icon: 'refresh' },
  ];

  const newScheduled = () => {
    setSendMode('schedule');
    setActiveTab('compose');
  };

  return (
    <div>
      {/* The heading belongs HERE, not in UpdateAllCard where it was first put:
          that component renders only on the refresh tab, so the messages tab had
          no heading at all and the refresh tab showed an <h1> halfway down the
          page, under the tab strip. */}
      <PageHeader
        description="Send a service message to users, or bring their workspaces up to date."
        actions={anyActive ? (
          <div className="row-wrap gap-2">
            {broadcastActive && <Badge tone="info" dot pulse>Broadcast in progress</Badge>}
            {updateActive && <Badge tone="info" dot pulse>Workspace refresh in progress</Badge>}
          </div>
        ) : null}
      >
        <h1 className="page-title">Broadcast</h1>
      </PageHeader>

      <div className="page-tabs">
        <Tabs tabs={tabs} value={activeTab} onChange={setActiveTab} variant="line"
              label="Broadcast sections" idPrefix="broadcast" />
      </div>

      {activeTab === 'compose' && (
        <div role="tabpanel" id="broadcast-panel-compose" aria-labelledby="broadcast-tab-compose">
          {/* Form on the left, summary on the right. The summary takes ~300px and the form
              the rest; once the form would drop below 60% of the row, both wrap to full
              width with the summary last, so a phone reads the form and ends on the button. */}
          <div className="row-wrap items-start gap-5">
            <section className="card mb-0" style={{ flex: '999 1 0%', minWidth: '60%' }}
                     aria-labelledby="bc-compose-title">
              <div className="card-header">
                <div>
                  <h2 className="card-title" id="bc-compose-title">
                    Service message
                    {/* The lede below already says "one message, on the channel each user last
                        used", so the tip carries only what it does not: the prefix, the two
                        fallback cases, and the fact that a send cannot be taken back. */}
                    <InfoTip label="About service messages" width={360}>
                      <p>
                        Opens with a tag in square brackets, <code>[{DEFAULT_TAG}]</code> unless
                        you reword it, so the user can tell it from their agent.
                      </p>
                      <p>
                        Never written? They get their first paired channel. No paired channel at
                        all? Skipped.
                      </p>
                      <p>
                        Paced per Telegram bot, so a large send takes a while. It cannot be
                        cancelled, and only one runs at a time.
                      </p>
                    </InfoTip>
                  </h2>
                  <p className="card-subtitle">
                    One message to active users, on the channel each last used or on one you pick.
                  </p>
                </div>
              </div>

              <div className="card-section">
                <div className="subhead">
                  <div>
                    <h3 className="subhead-title">Audience</h3>
                    <p className="subhead-desc">
                      Everyone active with a paired channel, unless you narrow it. Filters combine.
                    </p>
                  </div>
                  {hasFilter && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={clearFilters}>
                      <Icon name="x" /> Clear filters
                    </button>
                  )}
                </div>
                <FilterPicker
                  idPrefix="bc"
                  tiers={tiers}
                  coupons={coupons}
                  channels={channels}
                  selectedChannel={selectedChannel}
                  setSelectedChannel={setSelectedChannel}
                  selectedTierId={selectedTierId}
                  setSelectedTierId={setSelectedTierId}
                  selectedSource={selectedSource}
                  setSelectedSource={setSelectedSource}
                  selectedCouponCode={selectedCouponCode}
                  setSelectedCouponCode={setSelectedCouponCode}
                  couponPickerEnabled={couponPickerEnabled}
                />
              </div>

              <div className="card-section">
                <div className="subhead">
                  <div>
                    <h3 className="subhead-title"><label htmlFor="bc-message">Message</label></h3>
                    <p className="subhead-desc" id="bc-message-hint">
                      Markdown: {FORMAT_HINT}. The tag line goes above it.
                    </p>
                  </div>
                  <span id="bc-message-count"
                        className={cx('text-sm num nowrap', charsLeft < 100 ? 'text-danger' : 'text-muted')}>
                    {charsLeft.toLocaleString()} characters left
                  </span>
                </div>
                <Field label="Tag" htmlFor="bc-tag" className="mb-3"
                       hint="The words inside the brackets; the brackets always stay.">
                  <TagInput id="bc-tag" value={tag} onChange={setTag} />
                </Field>
                <textarea
                  id="bc-message"
                  className="form-input"
                  rows={8}
                  maxLength={MAX_LEN}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type the message users will receive…"
                  aria-describedby="bc-message-hint bc-message-count"
                />
              </div>

              <div className="card-section">
                <div className="subhead">
                  <h3 className="subhead-title" id="bc-delivery-title">Delivery</h3>
                </div>
                <div role="radiogroup" aria-labelledby="bc-delivery-title" className="grid-2 gap-3">
                  <label className="check-row card-inset">
                    <input type="radio" name="sendMode" value="now" checked={sendMode === 'now'}
                      onChange={() => setSendMode('now')} />
                    <span>
                      <span className="fw-500">Send now</span>
                      <span className="check-hint">Goes out as soon as you confirm it.</span>
                    </span>
                  </label>
                  <label className="check-row card-inset">
                    <input type="radio" name="sendMode" value="schedule" checked={sendMode === 'schedule'}
                      onChange={() => setSendMode('schedule')} />
                    <span>
                      <span className="fw-500">Schedule</span>
                      <span className="check-hint">At a time you pick, once or every few days.</span>
                    </span>
                  </label>
                </div>

                {sendMode === 'schedule' && (
                  <div className="form-row gap-4 mt-4">
                    <Field label="First send" htmlFor="bc-first" hint="In your local time." className="mb-0">
                      <input
                        id="bc-first"
                        type="datetime-local"
                        className="form-input"
                        value={firstTriggerAt}
                        onChange={(e) => setFirstTriggerAt(e.target.value)}
                      />
                    </Field>
                    <Field
                      label="Repeat every (days)"
                      htmlFor="bc-repeat"
                      className="mb-0"
                      extra={<span className="text-faint fw-400">(optional)</span>}
                      hint="Leave empty to send it once. Otherwise it goes out again every N days after the first send."
                    >
                      <input
                        id="bc-repeat"
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        className="form-input num"
                        value={repeatEveryDays}
                        onChange={(e) => setRepeatEveryDays(e.target.value)}
                        placeholder="Once"
                      />
                    </Field>
                  </div>
                )}
              </div>
            </section>

            {/* Sticky while the form scrolls, so the audience and the button stay in view
                beside a long message. It only sticks while the two sit side by side. */}
            <aside style={{ flex: '1 1 300px', position: 'sticky', top: 'var(--sp-4)' }}
                   aria-labelledby="bc-summary-title">
              <div className="card mb-0">
                <div className="card-header">
                  <h2 className="card-title" id="bc-summary-title">Summary</h2>
                </div>
                <dl className="stack gap-4 m-0">
                  <div>
                    <dt className="overline mb-1">Audience</dt>
                    <dd className="m-0">
                      {audienceParts.length ? (
                        <div className="row-wrap gap-1">
                          {audienceParts.map(p => (
                            <Badge key={p.key} tone="accent" title={p.value}>{p.label}: {p.value}</Badge>
                          ))}
                        </div>
                      ) : <span className="fw-500">Every active user</span>}
                      <div className="text-sm text-muted mt-1">
                        {audienceParts.length ? 'Active, with a paired channel.' : 'With at least one paired channel.'}
                      </div>
                    </dd>
                  </div>
                  <div>
                    <dt className="overline mb-1">Delivery</dt>
                    <dd className="m-0">
                      {delivery || <span className="text-faint">Pick when it first goes out.</span>}
                    </dd>
                  </div>
                  <div>
                    <dt className="overline mb-1">Preview</dt>
                    <dd className="m-0">
                      {message.trim() ? previewBlock(220)
                        : <span className="text-sm text-faint">Nothing written yet.</span>}
                      {preview.error && <p className="form-hint text-danger mt-1 mb-0">{preview.error}</p>}
                    </dd>
                  </div>
                </dl>

                {sendMode === 'now' && broadcastActive && (
                  <div className="notice notice-warn mt-4 mb-0" role="status">
                    <Icon name="alert" />
                    <div className="notice-body">
                      A broadcast is still sending, and only one runs at a time: one started now
                      would fail.
                    </div>
                  </div>
                )}

                <div className="card-footer stack gap-2">
                  {sendMode === 'now' ? (
                    <button
                      type="button"
                      className="btn btn-danger btn-block"
                      onClick={confirmAndSend}
                      disabled={!messageValid || sending}
                    >
                      {sending ? <><span className="spinner-sm" /> Sending…</> : <><Icon name="send" /> Send broadcast</>}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary btn-block"
                      onClick={createSchedule}
                      disabled={!canSchedule || sending}
                    >
                      {sending ? <><span className="spinner-sm" /> Scheduling…</> : <><Icon name="calendar" /> Schedule message</>}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary btn-block"
                    onClick={() => setPreviewOpen(true)}
                    disabled={!message.trim()}
                  >
                    <Icon name="eye" /> Full preview
                  </button>
                  {blocker && <p className="form-hint text-center m-0">{blocker}</p>}
                </div>
              </div>
            </aside>
          </div>
        </div>
      )}

      {activeTab === 'scheduled' && (
        <div role="tabpanel" id="broadcast-panel-scheduled" aria-labelledby="broadcast-tab-scheduled">
          <ScheduledList
            schedules={schedules}
            loaded={schedulesLoaded}
            tiers={tiers}
            onOpen={setViewing}
            onRefresh={loadSchedules}
            onState={setScheduleState}
            onCancel={cancelSchedule}
            onEdit={setEditing}
            onDelete={deleteSchedule}
            onNew={newScheduled}
          />
        </div>
      )}

      {activeTab === 'sent' && (
        <div role="tabpanel" id="broadcast-panel-sent" aria-labelledby="broadcast-tab-sent">
          <RecentRuns
            kind="message"
            title="Sent messages"
            subtitle="Every broadcast, sent now or on a schedule, newest first."
            emptyTitle="No broadcasts yet"
            emptyText="Messages you send, now or on a schedule, are listed here with their delivery counts."
            emptyAction={(
              <button type="button" className="btn btn-primary" onClick={() => setActiveTab('compose')}>
                Write a message
              </button>
            )}
            runs={messageRuns}
            loaded={runsLoaded}
            live={broadcastActive}
            onRefresh={loadRuns}
            onOpen={setViewing}
            tiers={tiers}
            page={messageRunsPage}
            setPage={setMessageRunsPage}
            total={messageRunsTotal}
            pageSize={RUNS_PAGE_SIZE}
          />
        </div>
      )}

      {activeTab === 'refresh' && (
        <div role="tabpanel" id="broadcast-panel-refresh" aria-labelledby="broadcast-tab-refresh">
          <UpdateAllCard active={updateActive || updating} onTrigger={triggerUpdate} />
          <RecentRuns
            kind="update"
            title="Refresh history"
            subtitle="Each workspace refresh, newest first."
            emptyTitle="No workspace refreshes yet"
            emptyText="Each refresh is listed here with how many workspaces it brought up to date."
            runs={updateRuns}
            loaded={runsLoaded}
            live={updateActive}
            onRefresh={loadRuns}
            tiers={tiers}
            page={updateRunsPage}
            setPage={setUpdateRunsPage}
            total={updateRunsTotal}
            pageSize={RUNS_PAGE_SIZE}
          />
        </div>
      )}

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Message preview"
             description="As Telegram shows it; RossHub and Teams render the same formatting.">
        {previewBlock(360)}
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setPreviewOpen(false)}>Close</button>
        </div>
      </Modal>

      <Modal open={!!viewing} onClose={() => setViewing(null)} title="Message as sent"
             description={viewing ? summarizeFilter(viewing.filter_summary ?? viewing.filter_snapshot, tiers) : undefined}>
        {viewing && (viewing.html
          ? <RenderedMessage html={viewing.html} maxHeight={420} />
          : <pre className="code-block pre-wrap break m-0">{viewing.message}</pre>)}
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setViewing(null)}>Close</button>
        </div>
      </Modal>

      {editing && (
        <ScheduleEditModal
          schedule={editing}
          tiers={tiers}
          coupons={coupons}
          channels={channels}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await loadSchedules(); }}
        />
      )}

      {confirmDialog}
    </div>
  );
}


function UpdateAllCard({ active, onTrigger }) {
  return (
    <section className="card" aria-labelledby="bc-refresh-title">
      <div className="card-header">
        <h2 className="card-title" id="bc-refresh-title">
          Refresh workspace files
          {/* The lede below covers "instructions and guides, nothing user-written, no
              restart". The tip adds the exact filenames, the reason this exists at all, and
              the batching. Dropped as recoverable from the code: it makes no cluster call,
              so no runtime image is pulled, and a hibernated agent is refreshed in place. */}
          <InfoTip label="About the workspace refresh" width={380}>
            <p>
              Platform files only: <code>SOUL.md</code>, <code>AGENTS.md</code>,{' '}
              <code>MAIN.md</code>, <code>TOOLS.md</code>, <code>SKILLS.md</code> and{' '}
              <code>.guides/</code>, skipping a <code>SOUL.md</code> or <code>AGENTS.md</code>{' '}
              the user customised. User files and the conversation are left alone.
            </p>
            <p>
              Agents already get these at their next wake when templates, tools or settings
              change; this pushes them now.
            </p>
            <p>
              Batched in parallel, one retry on failure, one refresh at a time.
            </p>
          </InfoTip>
        </h2>
      </div>
      <div className="row-wrap between gap-4">
        <p className="section-lede m-0" style={{ flex: '1 1 360px' }}>
          Brings every user&apos;s agent instructions and tool guides up to date with the
          current build. Nothing user-written is touched, customised instructions are
          skipped, and no agent is restarted.
        </p>
        <button type="button" className="btn btn-primary" onClick={onTrigger} disabled={active}>
          {active
            ? <><span className="spinner-sm" /> Refreshing workspaces…</>
            : <><Icon name="refresh" /> Refresh all workspaces</>}
        </button>
      </div>
    </section>
  );
}


function FilterPicker({
  idPrefix, compact = false,
  tiers, coupons, channels,
  selectedChannel, setSelectedChannel,
  selectedTierId, setSelectedTierId,
  selectedSource, setSelectedSource,
  selectedCouponCode, setSelectedCouponCode,
  couponPickerEnabled,
}) {
  const couponOptions = [
    { value: LEGACY_COUPON_CODE, label: LEGACY_COUPON_CODE },
    ...coupons.map(c => ({ value: c.code, label: c.code })),
  ];
  const fieldId = (name) => `${idPrefix}-${name}`;

  // Four across on the page; two by two in the narrower edit dialog.
  return (
    <div className={cx('form-row gap-4', !compact && 'cols-4')}>
      <div className="form-group mb-0">
        <label className="form-label" id={fieldId('channel-label')} htmlFor={fieldId('channel')}>Channel</label>
        <SearchableSelect
          id={fieldId('channel')}
          aria-labelledby={fieldId('channel-label')}
          aria-describedby={fieldId('channel-hint')}
          value={selectedChannel}
          onChange={setSelectedChannel}
          placeholder="All channels"
          allLabel="All channels"
          searchPlaceholder="Search channels…"
          options={channels}
        />
        <div className="form-hint" id={fieldId('channel-hint')}>
          {selectedChannel
            ? 'Only users paired there, and only there.'
            : 'Each user on the channel they last used.'}
        </div>
      </div>

      <div className="form-group mb-0">
        <label className="form-label" id={fieldId('source-label')} htmlFor={fieldId('source')}>Source</label>
        <SearchableSelect
          id={fieldId('source')}
          aria-labelledby={fieldId('source-label')}
          value={selectedSource}
          onChange={setSelectedSource}
          placeholder="All sources"
          allLabel="All sources"
          searchPlaceholder="Search sources…"
          options={SOURCE_OPTIONS}
        />
      </div>

      <div className="form-group mb-0">
        <label className="form-label" id={fieldId('tier-label')} htmlFor={fieldId('tier')}>Tier</label>
        <SearchableSelect
          id={fieldId('tier')}
          aria-labelledby={fieldId('tier-label')}
          value={selectedTierId}
          onChange={setSelectedTierId}
          placeholder="All tiers"
          allLabel="All tiers"
          searchPlaceholder="Search tiers…"
          options={tiers.map(t => ({ value: t.id, label: t.name }))}
        />
      </div>

      <div className="form-group mb-0">
        <label className="form-label" id={fieldId('coupon-label')} htmlFor={fieldId('coupon')}>Coupon</label>
        {/* Disabled, not hidden, under a non-coupon source: the backend rejects a coupon
            filter there, and a field that vanishes when another changes reads as a bug. */}
        <SearchableSelect
          id={fieldId('coupon')}
          aria-labelledby={fieldId('coupon-label')}
          aria-describedby={fieldId('coupon-hint')}
          disabled={!couponPickerEnabled}
          value={couponPickerEnabled ? selectedCouponCode : ''}
          onChange={setSelectedCouponCode}
          placeholder="All coupons"
          allLabel="All coupons"
          searchPlaceholder="Search coupons…"
          options={couponOptions}
        />
        <div className="form-hint" id={fieldId('coupon-hint')}>
          {couponPickerEnabled
            ? '“legacy” matches users who redeemed before the code was recorded.'
            : 'Set the source to Coupon or All sources to filter by coupon.'}
        </div>
      </div>
    </div>
  );
}


function ScheduledList({ schedules, loaded, tiers, onOpen, onRefresh, onState, onCancel, onEdit, onDelete, onNew }) {
  return (
    <section className="card card-flush" aria-labelledby="bc-schedules-title">
      <div className="card-header">
        <div>
          <h2 className="card-title" id="bc-schedules-title">
            Scheduled and recurring messages
            {/* All three of these are things that surprise people, so nothing is dropped
                here — only the words around them. */}
            <InfoTip label="About scheduled messages" width={360}>
              <p>
                Stores the <em>filter</em>, not a list of names: recipients are recomputed at
                every fire, so anyone newly eligible is included.
              </p>
              <p>
                Recurrence anchors to the first trigger, so changing that time re-anchors the
                whole series.
              </p>
              <p>
                A sweep runs every few minutes, so a fire can land late. Skipped entirely while
                a maintenance freeze is armed.
              </p>
            </InfoTip>
          </h2>
          <p className="card-subtitle">Messages waiting for their send time, and the ones that repeat.</p>
        </div>
        <div className="card-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onRefresh}>
            <Icon name="refresh" /> Refresh
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={onNew}>
            <Icon name="plus" /> New scheduled message
          </button>
        </div>
      </div>

      {!loaded ? (
        <TableSkeleton rows={3} cols={7} label="Loading scheduled messages" />
      ) : schedules.length === 0 ? (
        <EmptyState
          icon="calendar"
          title="No scheduled messages"
          action={<button type="button" className="btn btn-primary" onClick={onNew}>Schedule the first message</button>}
        >
          A scheduled message goes out at a time you pick, once or every few days.
        </EmptyState>
      ) : (
        <div className="table-container">
          <table className="table table-stack">
            <thead>
              <tr>
                <th>Next send</th>
                <th>Repeat</th>
                <th>State</th>
                <th className="num">Sent</th>
                <th>Audience</th>
                <th>Message</th>
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {schedules.map(s => {
                const terminal = s.state === 'completed' || s.state === 'cancelled';
                const audience = summarizeFilter(s.filter_snapshot, tiers);
                const when = formatWhen(s.next_trigger_at) || 'no date';
                return (
                  <tr key={s.id}>
                    <td data-label="Next send" className="nowrap">
                      <span className={cx('num', terminal && 'text-faint')} title={fullWhen(s.next_trigger_at)}>
                        {formatWhen(s.next_trigger_at) || '—'}
                      </span>
                      {s.last_triggered_at && (
                        <span className="cell-sub" title={fullWhen(s.last_triggered_at)}>
                          Last sent {formatWhen(s.last_triggered_at)}
                        </span>
                      )}
                    </td>
                    <td data-label="Repeat" className="nowrap">
                      {s.repeat_every_days
                        ? `Every ${s.repeat_every_days} day${s.repeat_every_days === 1 ? '' : 's'}`
                        : 'Once'}
                    </td>
                    <td data-label="State">{stateBadge(SCHEDULE_STATE, s.state)}</td>
                    <td data-label="Sent" className="num">{(s.trigger_count || 0).toLocaleString()}</td>
                    <td data-label="Audience">
                      <div className={cx('truncate text-sm', !s.filter_snapshot && 'text-muted')}
                           style={{ maxWidth: 160 }} title={audience}>
                        {audience}
                      </div>
                    </td>
                    <td data-label="Message">
                      <MessageCell row={s} maxWidth={220} onOpen={onOpen} />
                    </td>
                    {/* Third cell with this problem, and the subtlest. It was
                        right-aligned inline-blocks separated by `{' '}`, so Delete
                        DID line up — it is last against a right edge — but Pause (5
                        characters) versus Resume (6) shifted Edit and Cancel, and a
                        terminal row renders Delete alone. Four grid columns with the
                        state action in the `1fr` slot aligns all four; the spans hold
                        the slots a terminal row does not fill. */}
                    <td data-label="Actions">
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto auto auto',
                        gap: 'var(--sp-1)',
                        alignItems: 'center',
                      }}>
                        {!terminal && s.state === 'active' ? (
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onState(s.id, 'pause')}>
                            <Icon name="pause" /> Pause
                          </button>
                        ) : !terminal && s.state === 'paused' ? (
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onState(s.id, 'resume')}>
                            <Icon name="play" /> Resume
                          </button>
                        ) : <span />}
                        {!terminal ? (
                          <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={() => onEdit(s)}
                                  aria-label={`Edit the message scheduled for ${when}`} title="Edit">
                            <Icon name="edit" />
                          </button>
                        ) : <span />}
                        {!terminal ? (
                          <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={() => onCancel(s.id)}
                                  aria-label={`Cancel the message scheduled for ${when}`} title="Cancel schedule">
                            <Icon name="xCircle" />
                          </button>
                        ) : <span />}
                        <button type="button" className="btn btn-danger-soft btn-sm btn-icon" onClick={() => onDelete(s.id)}
                                aria-label={`Delete the message scheduled for ${when}`} title="Delete">
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


function ScheduleEditModal({ schedule, tiers, coupons, channels, onClose, onSaved }) {
  // The body and the tag apart, as the API returns them: the stored message joins them.
  const [message, setMessage] = useState(schedule.body ?? schedule.message ?? '');
  const [tag, setTag] = useState(schedule.tag || DEFAULT_TAG);
  const [firstTriggerAt, setFirstTriggerAt] = useState(isoToLocalInput(schedule.first_trigger_at));
  const [repeatEveryDays, setRepeatEveryDays] = useState(
    schedule.repeat_every_days == null ? '' : String(schedule.repeat_every_days)
  );
  const snap = schedule.filter_snapshot || {};
  const [selectedChannel, setSelectedChannel] = useState(snap.channel || '');
  const [selectedTierId, setSelectedTierId] = useState(snap.tier_id == null ? '' : snap.tier_id);
  const [selectedSource, setSelectedSource] = useState(snap.source || '');
  const [selectedCouponCode, setSelectedCouponCode] = useState(snap.coupon_code || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (selectedSource && selectedSource !== 'coupon' && selectedCouponCode) setSelectedCouponCode('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSource]);

  const couponPickerEnabled = !selectedSource || selectedSource === 'coupon';
  const charsLeft = MAX_LEN - message.length;
  const preview = usePreview(message, tag.trim() || DEFAULT_TAG);

  const buildFilterPayload = () => {
    const flt = {};
    if (selectedChannel) flt.channel = selectedChannel;
    if (selectedTierId !== '' && selectedTierId != null) flt.tier_id = Number(selectedTierId);
    if (selectedSource) flt.source = selectedSource;
    if (selectedCouponCode && couponPickerEnabled) flt.coupon_code = selectedCouponCode;
    return Object.keys(flt).length ? flt : null;
  };

  const save = async () => {
    if (!tag.trim()) { toast.error('The tag needs some words between the brackets'); return; }
    if (message.trim().length === 0) { toast.error('Message body is required'); return; }
    const iso = localInputToIso(firstTriggerAt);
    if (!iso) { toast.error('Pick a valid first trigger date & time'); return; }
    if (new Date(iso).getTime() <= Date.now()) { toast.error('First trigger must be in the future'); return; }
    if (repeatEveryDays !== '') {
      const n = Number(repeatEveryDays);
      if (!Number.isInteger(n) || n < 1) { toast.error('Repeat interval must be a whole number of days ≥ 1'); return; }
    }
    setSaving(true);
    try {
      const filter = buildFilterPayload();
      const body = { message: message.trim(), tag: tag.trim(), first_trigger_at: iso };
      if (repeatEveryDays === '') body.clear_repeat = true;
      else body.repeat_every_days = Number(repeatEveryDays);
      if (filter) body.filter = filter;
      else body.clear_filter = true;
      const data = await callApi(`/api/admin/broadcast/schedules/${schedule.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }, { successMsg: 'Scheduled message updated', errorMsg: 'Could not update' });
      if (data) await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={() => !saving && onClose()} title="Edit scheduled message" maxWidth={640}>
      <div className="overline mb-2">Audience</div>
      <FilterPicker
        idPrefix="bc-edit"
        compact
        tiers={tiers}
        coupons={coupons}
        channels={channels}
        selectedChannel={selectedChannel}
        setSelectedChannel={setSelectedChannel}
        selectedTierId={selectedTierId}
        setSelectedTierId={setSelectedTierId}
        selectedSource={selectedSource}
        setSelectedSource={setSelectedSource}
        selectedCouponCode={selectedCouponCode}
        setSelectedCouponCode={setSelectedCouponCode}
        couponPickerEnabled={couponPickerEnabled}
      />

      <div className="form-group mt-5">
        <div className="row between mb-1">
          <label className="form-label mb-0" htmlFor="bc-edit-message">Message</label>
          <span id="bc-edit-message-count"
                className={cx('text-sm num', charsLeft < 100 ? 'text-danger' : 'text-muted')}>
            {charsLeft.toLocaleString()} characters left
          </span>
        </div>
        <Field label="Tag" htmlFor="bc-edit-tag" className="mb-2">
          <TagInput id="bc-edit-tag" value={tag} onChange={setTag} />
        </Field>
        <textarea
          id="bc-edit-message"
          className="form-input"
          rows={6}
          maxLength={MAX_LEN}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          aria-describedby="bc-edit-message-count bc-edit-format"
        />
        <div className="form-hint" id="bc-edit-format">Markdown: {FORMAT_HINT}.</div>
        {preview.html && <div className="mt-2"><RenderedMessage html={preview.html} maxHeight={200} /></div>}
      </div>

      <div className="form-row gap-4">
        <Field label="First send" htmlFor="bc-edit-first" hint="In your local time." className="mb-0">
          <input id="bc-edit-first" type="datetime-local" className="form-input" value={firstTriggerAt}
            onChange={(e) => setFirstTriggerAt(e.target.value)} />
        </Field>
        <Field label="Repeat every (days)" htmlFor="bc-edit-repeat" className="mb-0"
               extra={<span className="text-faint fw-400">(optional)</span>}
               hint="Leave empty to send it once.">
          <input id="bc-edit-repeat" type="number" min={1} step={1} inputMode="numeric" className="form-input num"
            value={repeatEveryDays} onChange={(e) => setRepeatEveryDays(e.target.value)} placeholder="Once" />
        </Field>
      </div>

      <div className="notice notice-info mt-4 mb-0">
        <Icon name="info" />
        <div className="notice-body">
          Changing the first send restarts the schedule from that time. Recipients are worked
          out again at every send.
        </div>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? <><span className="spinner-sm" /> Saving…</> : 'Save schedule'}
        </button>
      </div>
    </Modal>
  );
}


function RecentRuns({
  kind, title, subtitle, emptyTitle, emptyText, emptyAction,
  runs, loaded, live, onRefresh, onOpen, tiers, page, setPage, total, pageSize,
}) {
  const isMessage = kind === 'message';
  const headingId = `bc-runs-${kind}`;
  const doneLabel = isMessage ? 'Sent' : 'Refreshed';
  const targetLabel = isMessage ? 'Recipients' : 'Users';
  return (
    <section className="card card-flush" aria-labelledby={headingId}>
      <div className="card-header">
        <div>
          <h2 className="card-title" id={headingId}>{title}</h2>
          <p className="card-subtitle">{subtitle}</p>
        </div>
        <div className="card-actions">
          {/* Re-polled every 5s while a run is in flight, which is why the counts move on
              their own; the badge says so rather than leaving it to look like a glitch. */}
          {live && <Badge tone="info" dot pulse>Live</Badge>}
          <button type="button" className="btn btn-secondary btn-sm" onClick={onRefresh}>
            <Icon name="refresh" /> Refresh
          </button>
        </div>
      </div>

      {!loaded ? (
        <TableSkeleton rows={4} cols={isMessage ? 7 : 5} label="Loading runs" />
      ) : total === 0 ? (
        <EmptyState icon={isMessage ? 'broadcast' : 'refresh'} title={emptyTitle} action={emptyAction}>
          {emptyText}
        </EmptyState>
      ) : (
        <>
          <div className="table-container">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Status</th>
                  <th className="num">{doneLabel}</th>
                  <th className="num">Failed</th>
                  <th className="num">{targetLabel}</th>
                  {/* A workspace refresh always targets everyone and always carries the
                      same marker text, so these two columns would repeat one value per row. */}
                  {isMessage && <th>Audience</th>}
                  {isMessage && <th>Message</th>}
                </tr>
              </thead>
              <tbody>
                {runs.map(r => {
                  const inFlight = r.status === 'running' || r.status === 'queued';
                  const processed = (r.success_count || 0) + (r.fail_count || 0);
                  const audience = summarizeFilter(r.filter_summary, tiers);
                  return (
                    <tr key={r.id}>
                      <td data-label="When" className="nowrap">
                        <span className="num" title={fullWhen(r.created_at)}>{formatWhen(r.created_at) || '—'}</span>
                        {r.sent_by && <span className="cell-sub">by {r.sent_by}</span>}
                      </td>
                      <td data-label="Status">
                        <div className="stack gap-1 items-start" style={{ minWidth: 104 }}>
                          {stateBadge(RUN_STATUS, r.status, inFlight)}
                          {/* `tone` pinned: ProgressBar's automatic tone turns red near 100%,
                              which is right for a quota and wrong for a job about to finish. */}
                          {inFlight && r.target_count > 0 && (
                            <ProgressBar value={processed} max={r.target_count} tone="accent"
                                         label={`${processed} of ${r.target_count} processed`} />
                          )}
                          {r.status === 'failed' && r.error && (
                            <span className="cell-sub truncate" style={{ maxWidth: 170 }} title={r.error}>
                              {r.error}
                            </span>
                          )}
                        </div>
                      </td>
                      <td data-label={doneLabel} className="num">{(r.success_count || 0).toLocaleString()}</td>
                      <td data-label="Failed" className={cx('num', r.fail_count ? 'text-danger' : 'text-muted')}>
                        {(r.fail_count || 0).toLocaleString()}
                      </td>
                      <td data-label={targetLabel} className="num">{(r.target_count || 0).toLocaleString()}</td>
                      {isMessage && (
                        <td data-label="Audience">
                          <div className={cx('truncate text-sm', !r.filter_summary && 'text-muted')}
                               style={{ maxWidth: 160 }} title={audience}>
                            {audience}
                          </div>
                        </td>
                      )}
                      {isMessage && (
                        <td data-label="Message">
                          <MessageCell row={r} maxWidth={290} onOpen={onOpen} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage}
                      noun={isMessage ? 'messages' : 'refreshes'} />
        </>
      )}
    </section>
  );
}
